// ============================================================
// IoTProtocols.jsx
//
// Course content page for "IoT Protocols" lesson. Rewritten to:
//   1) remove six dead imports (FaCloud, FaTerminal, FaNetworkWired,
//      MdOutlineSettingsEthernet, MdOutlineSystemUpdateAlt,
//      SiTypescript — none were ever rendered),
//   2) complete the MQTT code example — the original never declared
//      the `client` object or connected to Wi-Fi first, so it
//      wouldn't compile as written,
//   3) soften an overstated claim that HTTP/S offers "the strongest
//      built-in security" — MQTT and CoAP both support TLS/DTLS too;
//      HTTPS's advantage is tooling familiarity, not uniquely strong
//      security,
//   4) match the app-wide dark-purple/goldenrod look,
//   5) use arrow-function components and Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ForumIcon from "@mui/icons-material/Forum";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LockIcon from "@mui/icons-material/Lock";
import BoltIcon from "@mui/icons-material/Bolt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import LayersIcon from "@mui/icons-material/Layers";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const PROTOCOL_FEATURES = [
    { title: "MQTT", icon: <ForumIcon />, color: BRAND.gold, description: "The 'gold standard' for IoT. Uses a Publish/Subscribe model — extremely lightweight for battery-powered nodes." },
    { title: "CoAP", icon: <SwapHorizIcon />, color: BRAND.teal, description: "A RESTful protocol for constrained devices. Runs over UDP to minimize overhead and power consumption." },
    { title: "HTTP/S", icon: <LockIcon />, color: BRAND.gold, description: "Standard web communication. Heavier overhead than MQTT/CoAP, but the most widely supported tooling and libraries." },
    { title: "WebSocket", icon: <BoltIcon />, color: BRAND.teal, description: "Persistent full-duplex connection. Best for real-time control panels and bi-directional low-latency streams." },
];

const SELECTION_GUIDE = [
    { need: "Real-time Telemetry", protocol: "MQTT", reason: "Smallest packet overhead, easy cloud integration." },
    { need: "Low-Power UDP", protocol: "CoAP", reason: "Zero-handshake latency, RESTful structure." },
    { need: "Direct Web Access", protocol: "HTTP/S", reason: "Standard tooling, no broker required." },
    { need: "Live Control UI", protocol: "WebSocket", reason: "True bi-directional real-time stream." },
];

// Sub-components (arrow-function style, defined before first use)

const ProtocolFeature = ({ title, icon, color, description }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            background: BRAND.surface,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: color, transform: "translateY(-4px)" },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: `${color}26`, color }}>{icon}</Avatar>
            <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>{title}</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>{description}</Typography>
    </Paper>
);

const CodeTerminal = ({ code, label }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color: BRAND.gold, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Typography component="pre" sx={{ p: 2.5, color: "#FCD34D", fontSize: 13, fontFamily: "'Fira Code', monospace", overflowX: "auto" }}>
                {code}
            </Typography>
        </Box>
    );
};

// A complete, compiling sketch — the original never declared `client`
// or connected to Wi-Fi before using MQTT.
const MQTT_SKETCH = `#include <WiFi.h>
#include <PubSubClient.h>

WiFiClient espClient;
PubSubClient client(espClient);

void setup() {
  WiFi.begin("SSID", "PASSWORD");
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
  }

  client.setServer("mqtt.eclipseprojects.io", 1883);
  if (client.connect("ESP32_Node_A")) {
    client.publish("home/sensors/temp", "24.5");
    client.subscribe("home/actuators/led");
  }
}

void loop() {
  client.loop(); // Keep the connection alive and process incoming messages
}`;

// ── Main Page ───────────────────────────────────────────────────

const IoTProtocols = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Module 05: Advanced Networking"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    IoT Messaging <span style={{ color: BRAND.teal }}>Protocols</span>
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Protocols are the "languages" of IoT. They determine how data is formatted, secured, and
                    delivered across constrained and high-latency networks.
                </Typography>
            </Box>

            {/* PROTOCOL OVERVIEW */}
            <Box sx={{ mb: 10 }}>
                <Grid container spacing={3}>
                    {PROTOCOL_FEATURES.map((p) => (
                        <Grid item xs={12} md={3} key={p.title}>
                            <ProtocolFeature {...p} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* IMPLEMENTATION: MQTT */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(218,165,32,0.1)", border: `1px solid ${BRAND.gold}` }}>
                        <ForumIcon sx={{ color: BRAND.gold }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>MQTT Implementation</Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>Pub/Sub logic on ESP32</Typography>
                    </Box>
                </Box>

                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            In the <strong>Pub/Sub</strong> model, devices (clients) don't talk directly.
                            They send data to a <strong>broker</strong> on a specific <strong>topic</strong>.
                            Any other client subscribed to that topic receives the message instantly.
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(218,165,32,0.06)", border: `1px solid ${BRAND.gold}4d` }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.gold }}>LWT (Last Will & Testament)</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Notifies the broker if a device goes offline unexpectedly.</Typography>
                            </Box>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(45,212,191,0.06)", border: `1px solid ${BRAND.teal}4d` }}>
                                <Typography variant="subtitle2" sx={{ color: BRAND.teal }}>QoS (Quality of Service)</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Ensures message delivery at level 0, 1, or 2.</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal label="MQTT_PUBLISH.ino" code={MQTT_SKETCH} />
                    </Grid>
                </Grid>
            </Box>

            {/* COAP SECTION */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(45,212,191,0.1)", border: `1px solid ${BRAND.teal}` }}>
                        <SwapHorizIcon sx={{ color: BRAND.teal }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>CoAP & Constrained Environments</Typography>
                </Box>
                <Paper sx={{ p: 4, borderRadius: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}` }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 2 }}>Why CoAP?</Typography>
                            <Typography sx={{ color: BRAND.muted, fontSize: 14, lineHeight: 1.7 }}>
                                CoAP is essentially "HTTP for small things." It uses the same GET, POST, PUT, DELETE
                                methods but runs over <strong>UDP</strong> instead of TCP. That means smaller
                                packets and no handshake overhead, saving battery life on remote field sensors.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography sx={{ color: BRAND.white, fontWeight: 700, mb: 2 }}>Primary Use Case</Typography>
                            <Typography sx={{ color: BRAND.muted, fontSize: 14, lineHeight: 1.7 }}>
                                <strong>Smart agriculture:</strong> sensors buried in soil that wake up once an
                                hour to "POST" moisture data. CoAP's low binary overhead keeps the radio on for
                                the shortest time possible.
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* SELECTION MATRIX */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <CompareArrowsIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Protocol Selection Guide</Typography>
                </Box>
                <Grid container spacing={2}>
                    {SELECTION_GUIDE.map((item) => (
                        <Grid item xs={12} sm={6} key={item.need}>
                            <Box sx={{ p: 2, borderRadius: 3, border: `1px solid ${BRAND.border}`, bgcolor: "rgba(255,255,255,0.03)" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" sx={{ color: BRAND.muted }}>Need: {item.need}</Typography>
                                    <Chip label={item.protocol} size="small" sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 900 }} />
                                </Stack>
                                <Typography variant="caption" sx={{ color: BRAND.white, mt: 1, display: "block" }}>{item.reason}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <LayersIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Protocol Logic Mastered?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Configure your broker and start streaming data across the stack.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Launch MQTT Broker Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Protocol Comparison Chart" variant="outlined" onClick={() => {}} sx={{ borderColor: BRAND.muted, color: BRAND.white }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default IoTProtocols;
